import { Link } from 'react-router-dom';

import config from '@/config';
import { NextButton, PrevButton } from '@/shared-components/buttons/components/SliderControls';
import FHSlider from '@/shared-components/sliders/components/FHSlider';
import Utils from '@/utils';
import { formatArticleReadingTime } from '@/utils/articleReadingTime';
import { buildPublicContentPath } from '@/utils/contentRoutes';

import './styles/ArticleRankingSlider.css';

function buildArticleLink(article = {}) {
  return article._id ? buildPublicContentPath({ ...article, post_type: 'article' }, 'article') : '#';
}

function isRecentlyPublished(article = {}) {
  const created = article.createdAt ? new Date(article.createdAt) : null;
  if (!created || Number.isNaN(created.getTime())) return false;
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  return Date.now() - created.getTime() <= sevenDaysMs;
}

function ArticleRankingSlide({
  article,
  index,
}) {
  const coverImage = article.coverImageId
    ? `${config.app.imageServiceDomain}/${article.coverImageId}/w=1600`
    : null;
  const articleLink = buildArticleLink(article);
  const comments = article.comment_count ?? 0;
  const recent = isRecentlyPublished(article);
  const rankNumber = String(index + 1).padStart(2, '0');
  const readingTimeLabel = formatArticleReadingTime(article);

  return (
    <div className="article-ranking-slider__slide-wrapper">
      <Link to={articleLink} className="article-ranking-slider__slide">
        <div className="article-ranking-slider__media">
          {coverImage ? (
            <img src={coverImage} alt={article.title || 'Article cover'} loading="lazy" />
          ) : (
            <div className="article-ranking-slider__media-placeholder" aria-hidden="true" />
          )}
          <span className="article-ranking-slider__rank" aria-label={`Rank ${rankNumber}`}>
            {rankNumber}
          </span>
          {recent && <span className="article-ranking-slider__badge">New</span>}
        </div>

        <div className="article-ranking-slider__content">
          <h3 className="article-ranking-slider__title">{article.title}</h3>

          {article.description && (
            <p className="article-ranking-slider__summary">{article.description}</p>
          )}

          <p className="article-ranking-slider__meta">
            {comments > 0 && (
              <span>{Utils.numberParserMillionThousand(comments)} comments</span>
            )}
            <span>{readingTimeLabel}</span>
          </p>
        </div>
      </Link>
    </div>
  );
}

export default function ArticleRankingSlider({
  articles = [],
  category = '',
  rankingPeriod = 'weekly',
}) {
  if (!articles || articles.length === 0) {
    return null;
  }

  const rankingTitle =
    (typeof category === 'string' && category.trim().length > 0)
      ? category
      : (rankingPeriod === 'monthly'
        ? 'Top of the month'
        : rankingPeriod === 'all_time'
          ? 'Top of all time'
          : 'Top of the week');

  return (
    <section className="article-ranking-slider__container element-non-selectable">
      <header className="article-ranking-slider__header">
        <h2 className="article-ranking-slider__heading">{rankingTitle}</h2>
      </header>

      <FHSlider
        speed={1.4}
        className="article-ranking-slider__slider"
        customPrevButton={<PrevButton />}
        customNextButton={<NextButton />}
      >
        {articles.map((article, index) => (
          <ArticleRankingSlide
            key={article._id || article.slug || article.article_slug || index}
            article={article}
            index={index}
          />
        ))}
      </FHSlider>
    </section>
  );
}
